import { LitElement, createRef, css, html, ref } from '@lit'
import { storefrontClient } from '@nova/storefront'

class NovaSearchProduct extends LitElement {
  static properties = {
    product: { type: Object },
    productBadges: { type: Array },
    secondImage: { type: String },
    sku: { type: String },
    swatches: { type: Array },
    sizes: { type: Array },
    variants: { type: Array },
  }

  constructor() {
    super()
    this.product = {}
    this.productBadges = []
    this.secondImage = ''
    this.sku = ''
    this.swatches = []
    this.sizes = []
    this.variants = []
    this.productCardRef = createRef()
  }

  async firstUpdated() {
    await this.queryProductData()

    const productCard = this.productCardRef.value
    if (productCard) {
      productCard.variants = this.variants
      productCard.swatches = this.swatches
      productCard.sizes = this.sizes
    }
  }

  async queryProductData() {
    const getProductData = await storefrontClient.getProductById(
      this.product.id,
    )

    this.productBadges = getProductData?.metafields?.custom?.badges
    this.sku = getProductData?.variants[0]?.sku || ''

    if (getProductData?.variants)
      for (const variant of getProductData.variants) {
        if (variant.image.url !== this.product.featured_image.url) {
          this.secondImage = variant.image.url
          break
        }
      }

    if (getProductData?.options)
      for (const option of getProductData.options) {
        const optionName = option.name.toLowerCase()
        if (optionName === 'color' || optionName === 'material') {
          this.swatches.push(...option.values)
        } else if (optionName === 'size') {
          this.sizes.push(...option.values)
        }
      }

    if (getProductData?.variants)
      for (const variant of getProductData.variants) {
        const variantData = {
          available: variant.available,
          compare_at_price: variant.compareAtPrice,
          featured_image: {
            src: variant.image.url,
          },
          id: variant.id,
          option1: variant.selectedOptions[0]?.value,
          option2: variant.selectedOptions[1]?.value,
          option3: variant.selectedOptions[2]?.value,
          options: [
            variant.selectedOptions[0]?.value,
            variant.selectedOptions[1]?.value,
            variant.selectedOptions[2]?.value,
          ],
          price: variant.price,
          public_title: variant.title,
          sku: variant.sku,
        }

        this.variants.push(variantData)
      }
  }

  render() {
    return html`
      <nova-product-card
        ${ref(this.productCardRef)}
        id='${this.product.id}'
        handle='${this.product.handle}'
        url='/products/${this.product.handle}'
        available='${this.product.available}'
        title='${this.product.title}'
        vendor='${this.product.vendor}'
        price='${this.product.price_min}'
        ${Number(this.product.compare_at_price_min) > Number(this.product.price_min) ? html`compare-at-price='${this.product.compare_at_price_min}'` : ''}
        image='${this.product.featured_image.url}'
        image-alt='${this.product.featured_image.alt}'
        secondary-image='${this.secondImage}'
        .badges='${this.productBadges}'
        sku='${this.sku}'
        mode='predictive-search'
      >
        <nova-adaptive-image
          slot='primary-image'
          data-image='${this.product.featured_image.url}'
          data-sizes='(max-width: 400px) 246px, (max-width: 480px) 320px, (max-width: 640px) 400px, (max-width: 767px) 480px, (max-width: 944px) 240px, (max-width: 1280px) 400px, (max-width: 1440px) 400px, (max-width: 1600px) 480px, (max-width: 1920px) 560px, (max-width: 144rem) 652px, 652px'
          data-loading='lazy'
          data-width='652'
          data-aspect-ratio='1'
        >
        </nova-adaptive-image>
      </nova-product-card>
    `
  }

  static styles = css`
    :host {
      width: 25%;
    }
  `
}

customElements.define('nova-search-product', NovaSearchProduct)
